import React, { useEffect, useRef, useState } from "react";
import { isExperienceEditorActive, Placeholder, RichText } from "@sitecore-jss/sitecore-jss-react";
import Helmet from "react-helmet";
import deepEqual from "deep-equal";
import i18next from "i18next";
import { AwsRum, AwsRumConfig } from "aws-rum-web";
import { isHTML } from "./types/helpers.tsx";
import MarketBar from "./components/LanguageBarModule/MarketBar";
import { getCookieValue } from "./utils/window.utils";

const Layout = ({ route, context }) => {
  const REACT_VERSION = React.version;
  const mounted = useRef(true);
  const [forceRender, setForceRender] = useState(false);
  const [loading, setLoading] = useState(false);
  const matomoId = route?.fields["MatomoId"]?.value;
  const isPressReleasePage = route?.fields?.isPressReleasePage?.value;
  const matomoTagManagerContainer = route?.fields["MatomoTagManagerContainer"]?.value;
  const isBrowser = typeof window !== "undefined";
  const origin = isBrowser ? window.location.origin : "";
  const metaImageSrc = route?.fields["MetaImage"]?.value?.src;
  const logoValue = context?.ContextExtensions?.logo?.value;
  const [showLanguageBar, setShowLanguageBar] = useState(false);
  const [hasMarginClass, setHasMarginClass] = useState(false);
  const contextLanguage = context?.language;
  const imageUrl =
    metaImageSrc && metaImageSrc.indexOf("https") === -1 && isBrowser
      ? origin + (metaImageSrc || `/${logoValue}`)
      : metaImageSrc || (isBrowser ? `${origin}/${logoValue}` : logoValue);

  const currentCountryCode = context?.ContextExtensions?.phonePrefixCountryCode?.value;

  // const currentCountryCode = "FR"; // Default country code for testing

  const [country, setCountry] = useState(null);
  const [bannerData, setBannerData] = useState(null);

  // function to get user's country from IP address using ipapi.co with fallback to ipwho.is

  const getUserCountry = async () => {
    const cachedCountry = sessionStorage.getItem("userCountry");

    if (cachedCountry) {
      return cachedCountry;
    }

    try {
      const res = await fetch("https://ipapi.co/json/");

      if (!res.ok) {
        throw new Error("ipapi request failed");
      }

      const data = await res.json();
      const country = data.country;

      sessionStorage.setItem("userCountry", country);
      return country;
    } catch (err) {
      console.warn("ipapi failed, trying ipinfo.is as fallback", err);

      try {
        const resFallback = await fetch("https://ipinfo.io/json");

        if (!resFallback.ok) {
          throw new Error("ipinfo.is request failed");
        }

        const dataFallback = await resFallback.json();
        const fallbackCountry = dataFallback.country;

        if (fallbackCountry) {
          sessionStorage.setItem("userCountry", fallbackCountry);
        }

        return fallbackCountry;
      } catch (errFallback) {
        console.error("ipinfo.is fallback also failed:", errFallback);
        return null;
      }
    }
  };

  // Fetches the user's country based on their IP address
  // and retrieves the corresponding banner data from the API

  const fetchData = async () => {
    if (typeof window === "undefined") return;

    const currentMarketCode = currentCountryCode;
    const usersMarketCookie = getCookieValue("usersMarket");
    const usersCountryCode = await getUserCountry();

    setCountry(usersCountryCode);

    if (!usersCountryCode || !currentMarketCode) return;

    const condition1 = !usersMarketCookie && usersCountryCode !== currentMarketCode;

    const condition2 = usersMarketCookie && usersMarketCookie !== currentMarketCode;

    if (condition1 || condition2) {
      try {
        const res = await fetch(
          `/api/MarketMapping?countrycode=${usersCountryCode}&currentCountryCode=${currentCountryCode}&contextLanguage=${contextLanguage}`
        );

        if (!res.ok) {
          console.error("Error fetching data:", res.statusText);
          return;
        }

        const data = await res.json();
        setBannerData(data);
      } catch (err) {
        console.error("Error fetching banner data:", err);
      }
    }
  };

  useEffect(() => {
    if (document && typeof document != "undefined") {
      document.documentElement.lang = i18next && i18next.language ? i18next.language : "en";

      document.documentElement.dir = context?.ContextExtensions?.isRTL ? "rtl" : "ltr";
    }

    var d = document,
      g = d.createElement("link"),
      s = d.getElementsByTagName("head")[0];
    g.rel = "icon";
    g.type = "image/PNG";
    g.href =
      window.location?.protocol +
      `//` +
      window.location?.host +
      "/" +
      window.location?.pathname.split("/")[1] +
      "/" +
      context?.ContextExtensions?.favicon?.value;
    s.appendChild(g);

    fetchData();
  }, []);

  useEffect(() => {
    //AWS RUM
    // const awsStr= '(function(n,i,v,r,s,c,x,z){x=window.AwsRumClient={q:[],n:n,i:i,v:v,r:r,c:c};window[n]=function(c,p){x.q.push({c:c,p:p});};z=document.createElement(\'script\');z.async=true;z.src=s;document.head.insertBefore(z,document.head.getElementsByTagName(\'script\')[0]);})(\'cwr\',\'b8469ed1-337b-40a6-83b3-6fa7f7ceeb2c\',\'1.0.0\',\'eu-central-1\',\'https://client.rum.us-east-1.amazonaws.com/1.15.0/cwr.js\',{sessionSampleRate:1,identityPoolId:"eu-central-1:240b9da6-e224-433e-9e90-5a12a4b9764f",endpoint:"https://dataplane.rum.eu-central-1.amazonaws.com",telemetries:["performance","errors","http"],allowCookies:true,enableXRay:false});';
    // hardInjectScriptFromStringIntoHead(awsStr);

    if (context?.ContextExtensions?.headscript) {
      injectScriptsFromHTMLString(context?.ContextExtensions?.headscript?.value);
    }
    if (context?.ContextExtensions?.onetrustscript) {
      injectScriptsFromHTMLString(context?.ContextExtensions?.onetrustscript?.value);
    }
    if (route?.fields?.["Page Script"] && route?.fields?.["Page Script"]?.value && isHTML(route?.fields?.["Page Script"]?.value) == false) {
      hardInjectScriptFromStringIntoBody(route?.fields?.["Page Script"]?.value);
    }
    if (route?.fields?.["Head Script"] && route?.fields?.["Head Script"]?.value && isHTML(route?.fields?.["Head Script"]?.value) == false) {
      hardInjectScriptFromStringIntoHead(route?.fields?.["Head Script"]?.value);
    }
  }, []);

  function hardInjectScriptFromStringIntoHead(str) {
    const script = document.createElement("script");
    script.innerHTML = str;
    document.head.appendChild(script);
  }
  function hardInjectScriptFromStringIntoBody(str) {
    // document.head.innerHTML += str;
    const script = document.createElement("script");
    script.innerHTML = str;
    document.body.appendChild(script);
  }

  function injectScriptsFromHTMLString(str) {
    const tpl = document.createElement("template");
    tpl.innerHTML = str;

    tpl.content.childNodes.forEach((child) => {
      const node = cloneNode(child);
      document.head.appendChild(node);
    });
  }

  function cloneNode(node) {
    if (node.tagName === "SCRIPT") {
      const script = document.createElement("script");
      script.text = node.innerHTML;
      let i = -1,
        attrs = node.attributes,
        attr;
      while (++i < attrs.length) {
        attr = attrs[i];
        if (attr && attrs[i]) {
          if (attrs[i].nodeName && attr.value) {
            script.setAttribute((attr = attrs[i]).nodeName, attr.value);
          }
        }
      }
      script.setAttribute("async", true);
      return script;
    } else {
      return node.cloneNode();
    }
  }

  useEffect(() => {
    if (!matomoId || !matomoTagManagerContainer) {
      return;
    }

    var _mtm = (window._mtm = window._mtm || []);
    _mtm.push({ "mtm.startTime": new Date().getTime(), event: "mtm.Start" });
    var d = document,
      g = d.createElement("script"),
      s = d.getElementsByTagName("script")[0];
    g.type = "text/javascript";
    g.async = true;
    g.src = "https://matomo.cnhind.com/agce/js/container_" + matomoTagManagerContainer + ".js";
    s.parentNode.insertBefore(g, s);

    var _paq = (window._paq = window._paq || []);
    /* tracker methods like "setCustomDimension" should be called before "trackPageView" */
    _paq.push(["trackPageView"]);
    _paq.push(["enableLinkTracking"]);
    (function () {
      var u = "https://matomo.cnhind.com/agce/";
      _paq.push(["setTrackerUrl", u + "matomo.php"]);
      _paq.push(["setSiteId", matomoId]);
      var d = document,
        g = d.createElement("script"),
        s = d.getElementsByTagName("script")[0];
      g.type = "text/javascript";
      g.async = true;
      g.src = u + "matomo.js";
      s.parentNode.insertBefore(g, s);
    })();
  }, []);

  useEffect(() => {
    //let customElements = window.customElements;
    customElements.whenDefined("pfe-icon").then(function () {
      const PfeIcon = customElements.get("pfe-icon");
      const setName = "ivg";
      let newSetPath = "/-/media/Onhighwayicons/ivg-icons";

      // function to return the complete path of the icon
      function newResolveNameFn(iconName, setName, path) {
        return `${path}/${iconName}.svg`;
      }
      PfeIcon.addIconSet(setName, newSetPath, newResolveNameFn);

      PfeIcon.defaultThemeSetInfo = {
        path: newSetPath,
        resolveIconName: newResolveNameFn,
      };
    });
  }, []);

  useEffect(() => {
    let componentsLoaded = false;
    if (typeof window !== "undefined") {
      window.addEventListener("componentsLoaded", () => {
        const element = document.querySelector(".skeleton-page");
        {
          element ? element.remove() : "";
        }
        if (componentsLoaded) return;

        componentsLoaded = true;
        mounted.current = true;
        setForceRender(!forceRender);
        {
          element ? element.remove() : "";
        }
        const root = document.getElementById("root");
        root.style.display = "block";
      });
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const checkForMarginClasses = () => {
        const found = !!document.querySelector('[class*="margin-top__"], [class*="margin-bottom__"]');
        setHasMarginClass(found);
      };

      checkForMarginClasses();

      const observer = new MutationObserver(() => {
        checkForMarginClasses();
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return () => observer.disconnect();
    }
  }, []);

  /*useEffect(() => {
    if (typeof window !== "undefined") {
      //SKELETON-----------------------------------------------
      const element = document.querySelector(".skeleton-page");
      const root = document.querySelector("#root");
      setTimeout(() => {
        mounted.current = true;
        setForceRender(!forceRender);
        if (element) {
          element.remove();
        }
        if (root) {
          root.style.display = "block";
        }
      }, 100);
      return () => {
        mounted.current = false;
      };
    }
  }, [route]);*/

  //OLD LOADING---------------------------------------------
  // const element = document.querySelector('.loading-spinner');
  // useEffect(() => {
  //   setTimeout(() => {
  //     mounted.current = true
  //     setForceRender(!forceRender)
  //     {element ? element.remove() : ""}
  //   }, 1)
  //   return () => {
  //     mounted.current = false
  //   }
  // }, [route])

  function dateConverter(dateString) {
    if (!dateString) return null;

    const date = new Date(dateString);

    const offsetMinutes = date.getTimezoneOffset();
    const offsetHours = Math.abs(Math.floor(offsetMinutes / 60));
    const offsetMinutesRest = Math.abs(offsetMinutes % 60);

    const offsetSign = offsetMinutes > 0 ? "-" : "+";
    const offsetFormatted = `${offsetSign}${String(offsetHours).padStart(2, "0")}:${String(offsetMinutesRest).padStart(2, "0")}`;

    return date.toISOString().replace("Z", offsetFormatted);
  }

  const metaDateTime = route?.fields["MetaDateTime"]?.value ? dateConverter(route?.fields["MetaDateTime"]?.value) : "";
  const date = route?.fields?.Date?.value ? dateConverter(route?.fields?.Date?.value) : "";

  const finalDate = metaDateTime || date;

  const canonicalUrl = React.useMemo(() => {
    const metaUrl = route?.fields?.["MetaUrl"]?.value;
    const contextCanonical = context?.ContextExtensions?.canonical;

    if (metaUrl) {
      return metaUrl.startsWith("http") ? metaUrl : `https://${metaUrl}`;
    }

    if (contextCanonical) {
      return contextCanonical;
    }

    if (typeof window !== "undefined") {
      return window.location.href;
    }

    return "";
  }, [route, context]);

  return (
    <React.Fragment>
      {context?.ContextExtensions?.bodyscript && (
        <RichText field={context.ContextExtensions.bodyscript} style={{ display: "none" }}></RichText>
      )}
      <Helmet>
        <title>{`${route?.fields?.MetaTitle?.value} | ${route?.fields?.ExtendedMetaData?.fields?.BrandName?.value}`}</title>
        <meta name="keywords" content={route?.fields["MetaKeywords"]?.value} />
        <meta name="description" content={route?.fields["MetaBrandDescription"]?.value} />

        <meta
          property="og:title"
          content={`${route?.fields?.MetaTitle?.value} | ${route?.fields?.ExtendedMetaData?.fields?.BrandName?.value}`}
        />
        <meta
          property="og:url"
          content={
            route?.fields["MetaUrl"]?.value
              ? route?.fields["MetaUrl"]?.value.indexOf("https") == -1
                ? "https://" + route?.fields["MetaUrl"]?.value
                : route?.fields["MetaUrl"]?.value
              : typeof window !== "undefined" && window?.location?.href
          }
        />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:type" content={route?.fields["MetaType"]?.value || (isPressReleasePage ? "article" : "website")} />
        {isPressReleasePage && <meta property="article:published_time" content={finalDate} />}
        {isPressReleasePage && <meta property="article:modified_time" content={finalDate} />}
        {isPressReleasePage && <meta property="og:updated_time" content={finalDate} />}
        <meta property="og:description" content={route?.fields["MetaBrandDescription"]?.value} />
        <meta property="og:locale" content={context?.language} />

        <meta name="twitter:card" content={"summary"} />
        <meta name="twitter:site" content={route?.fields["MetaSite"]?.value} />
        <meta
          name="twitter:title"
          content={`${route?.fields?.MetaTitle?.value} | ${route?.fields?.ExtendedMetaData?.fields?.BrandName?.value}`}
        />
        <meta name="twitter:description" content={route?.fields["MetaBrandDescription"]?.value} />
        <meta name="twitter:image" content={imageUrl} />
        <link rel="canonical" href={canonicalUrl}></link>
        {(Array.isArray(route?.fields?.AlternateLinks) ? route?.fields?.AlternateLinks?.value : []).map((item, index) => (
          <link key={index} rel="alternate" hreflang={item.Name} href={item.Value}></link>
        ))}
      </Helmet>

      {mounted.current && (
        <header id="header-body-document">
          {bannerData && <MarketBar bannerData={bannerData} isRtl={context?.ContextExtensions?.isRTL} />}
          <Placeholder name="jss-header" rendering={route} />
        </header>
      )}
      {mounted.current && (
        <main style={{ display: hasMarginClass ? "flex" : "", flexDirection: hasMarginClass ? "column" : "" }}>
          {/* <Placeholder name="jss-verticalnavbar" rendering={route} data={route} /> */}
          <Placeholder
            name="jss-main"
            rendering={route}
            render={(items) => {
              if (items[0]) {
                const firstItem = items[0];
                items[0] = React.cloneElement(firstItem, { isFirst: true });
              }

              items.forEach((item, index) => {
                if (item?.props?.rendering?.componentName === "VerticalNavBar") {
                  const vbar = items[index];
                  items[index] = React.cloneElement(vbar, { data: route });
                }
              });

              return items;
            }}
          />
        </main>
      )}

      {mounted.current && (
        <footer>
          <Placeholder name="jss-footer" rendering={route} />
        </footer>
      )}
    </React.Fragment>
  );
};
const propsAreEqual = (prevProps, nextProps) => {
  if (deepEqual(prevProps.route, nextProps.route)) return true;
  return false;
};
export default React.memo(Layout, propsAreEqual);
// export default Layout
